import { useEffect, useRef, useState } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AlertIcon, CheckIcon, ChevronDownIcon, ChevronRightIcon, InboxIcon, PlusIcon, RoutineIcon } from '@/components/icons'
import type { PlanItem, Tray as TrayGroups } from '@/logic/plan'
import { TRAY_ORDER } from '@/logic/plan'
import { usePrefs, type TrayKey } from '@/store/prefs'
import { openQuickAdd } from '@/store/ui'
import { PlanCard, type CardContext } from './PlanCard'

const LABELS: Record<TrayKey, { count: string; head: string; add: string | null }> = {
  routines: { count: 'Routines', head: 'Routines', add: 'Add a routine' },
  done: { count: 'Done', head: 'Done earlier this week', add: null },
  notdone: { count: 'Not done', head: 'Not done', add: null },
  nodate: { count: 'No date', head: 'No date', add: 'Add an undated task' },
}

/** Each group's mark, on its count: what kind of not-on-a-day it is. */
const ICONS: Record<TrayKey, typeof InboxIcon> = { routines: RoutineIcon, done: CheckIcon, notdone: AlertIcon, nodate: InboxIcon }

/** Groups a card can be dropped on. Done and Not done take nothing. */
const DROPPABLE: TrayKey[] = ['routines', 'nodate']

function addTo(key: TrayKey) {
  if (key === 'routines') openQuickAdd({ kind: 'routine', hint: 'Add a routine' })
  else openQuickAdd({ hint: 'Add an undated task' })
}

function Count({ k, n, open, single, over }: { k: TrayKey; n: number; open: boolean; single: boolean; over: boolean }) {
  const toggle = usePrefs((s) => s.toggleGroup)
  const { setNodeRef } = useDroppable({ id: `count:${k}`, data: { container: `tray:${k}` }, disabled: !DROPPABLE.includes(k) })
  // red only while it holds something: a filter can bring Not done's count to 0
  const danger = k === 'notdone' && !open && n > 0
  const Icon = ICONS[k]
  return (
    <button
      ref={setNodeRef}
      type="button"
      className={`tray__count ${danger ? 'tray__count--danger' : ''} ${over ? 'tray__count--over' : ''}`}
      aria-expanded={open}
      onClick={() => toggle(k, single)}
    >
      <Icon size={14} weight={2.3} />
      {LABELS[k].count} <b>{n}</b>
    </button>
  )
}

/**
 * The drawer's list scrolls in a box about four and a half rows tall; a fade at the bottom says there
 * is more until the end is reached.
 */
function useMoreBelow() {
  const ref = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 4)
    check()
    el.addEventListener('scroll', check, { passive: true })
    const ro = new ResizeObserver(check)
    ro.observe(el)
    for (const child of Array.from(el.children)) ro.observe(child)
    return () => {
      el.removeEventListener('scroll', check)
      ro.disconnect()
    }
  }, [])
  return { ref, more }
}

/** An open group in the drawer: the list alone, in its scrolling box; the count above names it. */
function DrawerGroup({ k, items, ctx, over }: { k: TrayKey; items: PlanItem[]; ctx: CardContext; over: boolean }) {
  const { setNodeRef } = useDroppable({ id: `group:${k}`, data: { container: `tray:${k}` }, disabled: !DROPPABLE.includes(k) })
  const live = items.filter((i) => !i.done && !i.faint).map((i) => i.task.id)
  const { ref: scrollRef, more } = useMoreBelow()
  const add = LABELS[k].add
  return (
    <section ref={setNodeRef} className={`tray__drawer-group ${over ? 'drop-hot' : ''}`} aria-label={LABELS[k].head}>
      {/* the group's own add, above its list, where the button row has no room for it */}
      {add && !ctx.readOnly && (
        <button type="button" className="tray__add-row" onClick={() => addTo(k)}>
          <PlusIcon size={16} weight={2.4} />
          {add}
        </button>
      )}
      <div ref={scrollRef} className={`tray__scroll ${more ? 'tray__scroll--more' : ''}`} data-sheet-swipe="off">
        <SortableContext items={live} strategy={verticalListSortingStrategy}>
          <div className="plist plist--flat">
            {items.map((item) => (
              <PlanCard key={item.key} item={item} where={{ notDone: k === 'notdone' }} ctx={ctx} container={`tray:${k}`} showDoneDay={k === 'done'} />
            ))}
          </div>
        </SortableContext>
        {items.length === 0 && <p className="tray__empty">Nothing here matches the filters.</p>}
      </div>
    </section>
  )
}

function Group({ k, items, ctx, flat, over }: { k: TrayKey; items: PlanItem[]; ctx: CardContext; flat: boolean; over: boolean }) {
  const { setNodeRef } = useDroppable({ id: `group:${k}`, data: { container: `tray:${k}` }, disabled: !DROPPABLE.includes(k) })
  const label = LABELS[k]
  const live = items.filter((i) => !i.done && !i.faint).map((i) => i.task.id)
  return (
    <section ref={setNodeRef} className={`tray__group ${flat ? 'tray__group--flat' : ''} ${over ? 'drop-hot' : ''}`} aria-label={label.head}>
      <div className="tray__ghead">
        <span className="eyebrow">{label.head}</span>
        <span className="tray__gcount">{items.length}</span>
        <span className="tray__spacer" />
        {label.add && !ctx.readOnly && (
          <button type="button" className="icon-btn tray__add" aria-label={label.add} onClick={() => addTo(k)}>
            <PlusIcon size={18} weight={2.2} />
          </button>
        )}
      </div>
      <SortableContext items={live} strategy={verticalListSortingStrategy}>
        <div className="plist plist--flat">
          {items.map((item) => (
            <PlanCard key={item.key} item={item} where={{ notDone: k === 'notdone' }} ctx={ctx} container={`tray:${k}`} showDoneDay={k === 'done'} />
          ))}
        </div>
      </SortableContext>
      {items.length === 0 && <p className="tray__empty">Nothing here matches the filters.</p>}
    </section>
  )
}

interface TrayProps {
  tray: TrayGroups
  ctx: CardContext
  /** Phone Columns: one group at a time, to keep the days in view. */
  single: boolean
  /** Flat inside a wide tray column or the phone Columns panel; floating cards in Stacked. */
  flat: boolean
  /** A phone's Stacked layout: the drawer under the top row, one group at a time in a scrolling box. */
  drawer?: boolean
  /** The groups with anything in them before the filters; a filter shows a group's count as 0, never hides it. */
  present?: TrayKey[]
  overContainer: string | null
}

/**
 * The tray: one line of counts in the order Routines, Done, Not done, No date,
 * empty groups hidden. Tapping a count opens its group below the line; open
 * groups stack in tray order.
 */
export function Tray({ tray, ctx, single, flat, overContainer, drawer = false, present }: TrayProps) {
  const openGroups = usePrefs((s) => s.openGroups)
  const shown = present ?? TRAY_ORDER.filter((k) => tray[k].length > 0)
  if (!shown.length) return null
  if (drawer) {
    // one group at a time: the first open one that has anything in it
    const open = shown.find((k) => openGroups.includes(k)) ?? null
    return (
      <div className="tray tray--drawer">
        <div className="tray__line" role="group" aria-label="Tray">
          {shown.map((k) => (
            <Count key={k} k={k} n={tray[k].length} open={open === k} single over={overContainer === `tray:${k}`} />
          ))}
        </div>
        {open && <DrawerGroup key={open} k={open} items={tray[open]} ctx={ctx} over={overContainer === `tray:${open}`} />}
      </div>
    )
  }
  return (
    <div className={`tray ${flat ? 'tray--flat' : ''}`}>
      <div className="tray__line" role="group" aria-label="Tray">
        {shown.map((k) => (
          <Count key={k} k={k} n={tray[k].length} open={openGroups.includes(k)} single={single} over={overContainer === `tray:${k}`} />
        ))}
      </div>
      {shown
        .filter((k) => openGroups.includes(k))
        .map((k) => (
          <Group key={k} k={k} items={tray[k]} ctx={ctx} flat={flat} over={overContainer === `tray:${k}`} />
        ))}
    </div>
  )
}

/** A wide screen's tray, collapsed to a strip of counts until opened. */
export function TrayStrip({ tray, present }: { tray: TrayGroups; present?: TrayKey[] }) {
  const setOpen = usePrefs((s) => s.setTrayColumnOpen)
  const openGroup = usePrefs((s) => s.openGroup)
  const shown = present ?? TRAY_ORDER.filter((k) => tray[k].length > 0)
  return (
    <section className="tray-strip" aria-label="Tray, collapsed">
      <button type="button" className="icon-btn" aria-label="Open the tray" aria-expanded={false} onClick={() => setOpen(true)}>
        <ChevronRightIcon size={18} weight={2.4} />
      </button>
      <span className="tray-strip__label">Tray</span>
      {shown.map((k) => (
        <StripCount key={k} k={k} n={tray[k].length} onOpen={() => {
          openGroup(k, false)
          setOpen(true)
        }} />
      ))}
    </section>
  )
}

function StripCount({ k, n, onOpen }: { k: TrayKey; n: number; onOpen: () => void }) {
  const { setNodeRef } = useDroppable({ id: `strip:${k}`, data: { container: `tray:${k}` }, disabled: !DROPPABLE.includes(k) })
  return (
    <button ref={setNodeRef} type="button" className={`tray-strip__count ${k === 'notdone' ? 'tray__count--danger' : ''}`} onClick={onOpen}>
      {LABELS[k].count} <b>{n}</b>
    </button>
  )
}

/** The open wide tray column's heading, which folds it back to a strip. */
export function TrayColumnHead() {
  const setOpen = usePrefs((s) => s.setTrayColumnOpen)
  return (
    <div className="tray-col__head">
      <button type="button" className="tray-col__toggle" aria-expanded onClick={() => setOpen(false)}>
        <ChevronDownIcon size={16} weight={2.4} />
        <span className="eyebrow">Tray</span>
      </button>
    </div>
  )
}
